import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppState, Pressable, StyleSheet, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Ionicons from "@expo/vector-icons/Ionicons";
import { useTranslation } from "react-i18next";

import { openAppSettings } from "@/lib/location-access";
import {
  NOTIFICATION_NUDGE_STORAGE_KEY,
  parseNudgeMemory,
  serializeNudgeMemory,
  shouldShowNudge,
  shownMemory,
  withLoweredFloor,
  type NudgeActivity,
  type NudgeMemory,
} from "@/lib/notification-nudge";
import {
  getNotificationPermission,
  registerForPushNotifications,
} from "@/lib/notifications";
import { storage } from "@/lib/storage";
import { useSessionStore } from "@/state/session-store";
import { useAppTheme } from "@/theme/app-theme";

/**
 * The "notifications are off" reminder — a small dismissable banner above the
 * tab bar. The decision of WHEN lives in `lib/notification-nudge.ts` (pure,
 * tested); this component wires it to the store's activity counters, the OS
 * permission, and device storage.
 *
 * Tapping the body runs the same recovery the settings toggles do: re-ask the
 * OS while asking is still possible, otherwise open the app's system settings
 * page — the only place left that can change the answer. Permission is
 * re-read on every foreground, because the OS does not tell the app about a
 * change made in settings; you have to look again.
 */
export function NotificationNudge() {
  const insets = useSafeAreaInsets();
  const { colors } = useAppTheme();
  const { t } = useTranslation();

  const unreadByChat = useSessionStore((state) => state.unreadByChat);
  const inbox = useSessionStore((state) => state.inbox);
  const groupInbox = useSessionStore((state) => state.groupInbox);

  const activity = useMemo<NudgeActivity>(
    () => ({
      unreadMessages: Object.values(unreadByChat).reduce((sum, n) => sum + n, 0),
      pendingCues: inbox.length,
      groupUnread: groupInbox.reduce(
        (sum, group) => sum + (group.unreadCount ?? 0),
        0,
      ),
    }),
    [unreadByChat, inbox, groupInbox],
  );

  const [granted, setGranted] = useState<boolean | null>(null);
  const [memory, setMemory] = useState<NudgeMemory | null>(null);
  const [visible, setVisible] = useState(false);
  // Write-behind guard: persisting the floor must not loop through the effect.
  const persisting = useRef(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      const [permission, raw] = await Promise.all([
        getNotificationPermission(),
        storage.get(NOTIFICATION_NUDGE_STORAGE_KEY),
      ]);
      if (!active) return;
      setGranted(permission.status === "granted");
      setMemory(parseNudgeMemory(raw));
    })();
    const sub = AppState.addEventListener("change", (state) => {
      if (state !== "active") return;
      void getNotificationPermission().then((permission) => {
        if (active) setGranted(permission.status === "granted");
      });
    });
    return () => {
      active = false;
      sub.remove();
    };
  }, []);

  useEffect(() => {
    if (granted === null || memory === null) return;
    if (granted) {
      // Nothing left to say; also hide an already-shown banner mid-flight.
      setVisible(false);
      return;
    }
    if (persisting.current) return;

    if (
      !visible &&
      shouldShowNudge({ permissionGranted: granted, now: Date.now(), memory, activity })
    ) {
      const next = shownMemory(Date.now(), activity);
      persisting.current = true;
      setMemory(next);
      setVisible(true);
      void storage
        .set(NOTIFICATION_NUDGE_STORAGE_KEY, serializeNudgeMemory(next))
        .finally(() => {
          persisting.current = false;
        });
      return;
    }

    // Counters dropped below the remembered baseline: the user read things.
    // Lower the floor so the next, smaller wave of activity still counts.
    const lowered = withLoweredFloor(memory, activity);
    if (
      lowered.seen.unreadMessages !== memory.seen.unreadMessages ||
      lowered.seen.pendingCues !== memory.seen.pendingCues ||
      lowered.seen.groupUnread !== memory.seen.groupUnread
    ) {
      persisting.current = true;
      setMemory(lowered);
      void storage
        .set(NOTIFICATION_NUDGE_STORAGE_KEY, serializeNudgeMemory(lowered))
        .finally(() => {
          persisting.current = false;
        });
    }
  }, [granted, memory, activity, visible]);

  const enable = useCallback(async () => {
    const current = await getNotificationPermission();
    if (current.status === "granted") {
      setGranted(true);
      return;
    }
    if (!current.canAskAgain) {
      // The OS prompt is spent; only the settings page can change the answer.
      // The foreground listener above re-reads the result on the way back.
      await openAppSettings();
      return;
    }
    await registerForPushNotifications();
    const after = await getNotificationPermission();
    setGranted(after.status === "granted");
  }, []);

  if (!visible || granted !== false) return null;

  return (
    <View
      pointerEvents="box-none"
      style={[styles.wrap, { bottom: insets.bottom + 72 }]}
    >
      <View
        style={[
          styles.banner,
          { backgroundColor: colors.surface, borderColor: colors.border },
        ]}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("profile.notifications.nudgeBody")}
          onPress={() => void enable()}
          style={styles.body}
        >
          <Ionicons
            name="notifications-off-outline"
            size={16}
            color={colors.textSecondary}
          />
          <Text
            style={[styles.text, { color: colors.text }]}
            numberOfLines={2}
          >
            {t("profile.notifications.nudgeBody")}
          </Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("profile.notifications.nudgeDismissA11y")}
          hitSlop={10}
          onPress={() => setVisible(false)}
          style={styles.dismiss}
        >
          <Ionicons name="close" size={16} color={colors.textSecondary} />
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: "absolute",
    left: 16,
    right: 16,
    alignItems: "center",
  },
  banner: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    paddingVertical: 8,
    paddingLeft: 12,
    paddingRight: 6,
    maxWidth: 420,
    shadowColor: "#000",
    shadowOpacity: 0.18,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 6,
  },
  body: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 1,
  },
  text: {
    fontSize: 12.5,
    lineHeight: 17,
    flexShrink: 1,
  },
  dismiss: {
    marginLeft: 8,
    padding: 4,
  },
});
